import { motion } from "framer-motion";
import { cn } from "@/lib/cn";

type OrbProps = {
  size?: number;
  intensity?: "idle" | "thinking" | "pulse";
  className?: string;
};

export function Orb({ size = 280, intensity = "idle", className }: OrbProps) {
  const pulseScale =
    intensity === "pulse" ? [1, 1.15, 1] : intensity === "thinking" ? [1, 1.06, 1] : [1, 1.03, 1];
  const pulseDuration = intensity === "pulse" ? 1.2 : intensity === "thinking" ? 2.2 : 5;

  return (
    <div
      className={cn("relative inline-flex items-center justify-center", className)}
      style={{ width: size, height: size }}
    >
      <div
        aria-hidden
        className="absolute inset-0 rounded-full blur-3xl opacity-70"
        style={{
          background:
            "radial-gradient(circle at 30% 30%, #5EEAFF 0%, #1C7FFF 35%, #003A80 70%, transparent 100%)",
        }}
      />
      <motion.div
        aria-hidden
        className="absolute rounded-full"
        style={{
          width: size * 0.92,
          height: size * 0.92,
          background:
            "conic-gradient(from 0deg, #5EEAFF, #1C7FFF, #3E9BFF, #5EEAFF, #0057C2, #5EEAFF)",
          filter: "blur(18px)",
        }}
        animate={{ rotate: 360 }}
        transition={{ duration: intensity === "thinking" ? 4 : 16, repeat: Infinity, ease: "linear" }}
      />
      <motion.div
        className="relative rounded-full overflow-hidden"
        style={{
          width: size * 0.82,
          height: size * 0.82,
          background:
            "radial-gradient(circle at 35% 30%, rgba(255,255,255,0.85) 0%, #5EEAFF 15%, #1C7FFF 50%, #0A1A4A 100%)",
          boxShadow:
            "inset -30px -30px 60px rgba(0,0,0,0.6), inset 20px 20px 50px rgba(255,255,255,0.2), 0 20px 80px rgba(28,127,255,0.4)",
        }}
        animate={{ scale: pulseScale }}
        transition={{ duration: pulseDuration, repeat: Infinity, ease: "easeInOut" }}
      >
        <motion.div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 70% 70%, rgba(94,234,255,0.6) 0%, transparent 40%)",
            mixBlendMode: "screen",
          }}
          animate={{ rotate: [0, 360], scale: [1, 1.1, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 20% 80%, rgba(62,155,255,0.5) 0%, transparent 50%)",
            mixBlendMode: "screen",
          }}
          animate={{ rotate: [360, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          aria-hidden
          className="absolute rounded-full"
          style={{
            top: "12%",
            left: "20%",
            width: "35%",
            height: "25%",
            background: "radial-gradient(ellipse, rgba(255,255,255,0.6), transparent 70%)",
            filter: "blur(8px)",
          }}
          animate={{ opacity: [0.4, 0.8, 0.4], x: [0, 8, 0], y: [0, -4, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>

      {intensity === "pulse" && (
        <motion.div
          aria-hidden
          className="absolute rounded-full border border-cyan-glow/40"
          style={{ width: size, height: size }}
          initial={{ scale: 0.9, opacity: 0.8 }}
          animate={{ scale: 1.4, opacity: 0 }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeOut" }}
        />
      )}
    </div>
  );
}
